import { useEffect, useState } from 'react';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  Phone, Save, CheckCircle2, XCircle, Loader2, Filter, User,
  ShieldCheck, KeyRound, Copy,
} from 'lucide-react';

/**
 * PhoneVerificationsPage — reverse SMS verification settings and queue.
 */
export default function PhoneVerificationsPage() {
  const [settings, setSettings] = useState(null);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);
  const [queue, setQueue] = useState({ items: [], counts: {} });
  const [status, setStatus] = useState('waiting_sms');
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState({});
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const loadAll = async () => {
    try {
      const [sRes, qRes] = await Promise.all([
        api.get('/api/admin/phone-verifications/settings'),
        api.get(`/api/admin/phone-verifications${status ? `?status=${status}` : ''}`),
      ]);
      setSettings(sRes.data);
      setSettingsDraft((d) => (d == null ? sRes.data : d));
      setQueue(qRes.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const saveSettings = async () => {
    if (settingsDraft?.enabled && !settingsDraft?.reverse_sms_enabled) {
      toast.error('Enable reverse SMS verification first');
      return;
    }
    if (settingsDraft?.reverse_sms_enabled && !settingsDraft?.reverse_sms_receiving_number) {
      toast.error('Receiving SMS number is required');
      return;
    }
    setSavingSettings(true);
    try {
      const r = await api.put('/api/admin/phone-verifications/settings', settingsDraft);
      setSettings(r.data);
      setSettingsDraft(r.data);
      toast.success('Verification settings saved');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSavingSettings(false);
    }
  };

  const copyText = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error('Copy failed');
    }
  };

  const approve = async (id) => {
    setActioning((a) => ({ ...a, [id]: 'approve' }));
    try {
      await api.post(`/api/admin/phone-verifications/${id}/approve`);
      toast.success('Verification approved');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Approve failed');
    } finally {
      setActioning((a) => ({ ...a, [id]: null }));
    }
  };

  const reject = async () => {
    if (!rejectModal) return;
    const id = rejectModal;
    setActioning((a) => ({ ...a, [id]: 'reject' }));
    try {
      await api.post(`/api/admin/phone-verifications/${id}/reject`, { reason: rejectReason });
      toast.success('Verification rejected');
      setRejectModal(null);
      setRejectReason('');
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reject failed');
    } finally {
      setActioning((a) => ({ ...a, [id]: null }));
    }
  };

  const fmt = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtFP = (v) => `${Math.round(Number(v || 0)).toLocaleString()} FP`;
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="space-y-5" data-testid="phone-verifications-page">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <Phone className="w-6 h-6 text-[#25D366]" /> Verify Phone Numbers
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          Reverse SMS verification only. Players text a generated code to your receiving number, then the SMS Monitor webhook verifies the matching sender automatically.
        </p>
      </div>

      <div className="rounded-xl p-4 border border-cyan-400/20 bg-[#0E1822] space-y-4"
           data-testid="reverse-sms-settings-panel">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-white">
              <ShieldCheck className="w-4 h-4 text-cyan-300" /> Reverse SMS Verification
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Players enter their number, then text a code to your receiving SMS number. The SMS Monitor extension posts the inbox message back here for automatic verification.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer shrink-0">
            <input
              type="checkbox"
              checked={!!settingsDraft?.reverse_sms_enabled}
              onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), reverse_sms_enabled: e.target.checked }))}
              data-testid="reverse-sms-enabled-toggle"
            />
            Enabled
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-400 mb-1">Receiving SMS Number</label>
            <input
              type="tel"
              placeholder="+1 619 604 2327"
              value={settingsDraft?.reverse_sms_receiving_number ?? ''}
              onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), reverse_sms_receiving_number: e.target.value }))}
              className="w-full px-3 py-2 bg-[#0B1118] border border-cyan-400/20 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-300"
              data-testid="reverse-sms-receiving-number-input"
            />
            <p className="text-[10px] text-gray-500 mt-1">This is the number players must text.</p>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Webhook Secret</label>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cyan-300" />
              <input
                type="password"
                placeholder={settingsDraft?.has_sms_monitor_webhook_secret ? settingsDraft.sms_monitor_webhook_secret_masked || 'Already set' : 'Paste a strong secret'}
                value={settingsDraft?.sms_monitor_webhook_secret ?? ''}
                onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), sms_monitor_webhook_secret: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 bg-[#0B1118] border border-cyan-400/20 rounded-lg text-sm text-white focus:outline-none focus:border-cyan-300"
                data-testid="reverse-sms-webhook-secret-input"
              />
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Leave blank to keep the current secret.</p>
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">Extension Webhook URL</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={`${window.location.origin}${settingsDraft?.sms_monitor_webhook_url || '/api/webhooks/sms-monitor'}`}
                className="min-w-0 flex-1 px-3 py-2 bg-[#0B1118] border border-white/10 rounded-lg text-xs text-gray-300"
                data-testid="reverse-sms-webhook-url"
              />
              <button
                type="button"
                onClick={() => copyText(`${window.location.origin}${settingsDraft?.sms_monitor_webhook_url || '/api/webhooks/sms-monitor'}`, 'Webhook URL')}
                className="px-3 rounded-lg border border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/10"
                data-testid="reverse-sms-copy-webhook-url"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <p className="text-[10px] text-gray-500 mt-1">Use this URL in the Chrome extension webhook settings.</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-white/10 pt-3">
          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settingsDraft?.enabled}
              onChange={(e) => setSettingsDraft((d) => ({ ...(d || {}), enabled: e.target.checked }))}
              data-testid="verify-settings-enabled-toggle"
            />
            Require phone verification before first withdrawal
          </label>
          <button
            onClick={saveSettings}
            disabled={savingSettings}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-[#0B0E14]"
            style={{ background: 'linear-gradient(135deg, #D4AF37, #F5D76E)' }}
            data-testid="verify-settings-save-btn"
          >
            {savingSettings ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </button>
        </div>
      </div>

      {/* ── Queue ── */}
      <div className="flex items-center gap-2 text-xs">
        {['waiting_sms', 'approved', 'rejected', 'expired'].map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            data-testid={`verify-tab-${s}`}
            className={`px-3 py-1.5 rounded-full border capitalize ${
              status === s
                ? 'bg-[#25D366]/20 border-[#25D366] text-green-300'
                : 'bg-[#131822] border-white/10 text-gray-400 hover:text-gray-200'
            }`}
          >
            {s === 'waiting_sms' ? 'Waiting SMS' : s} ({queue.counts?.[s] ?? 0})
          </button>
        ))}
        <span className="text-gray-600 text-[10px] ml-2 flex items-center gap-1"><Filter className="w-3 h-3" /> status filter</span>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-gray-400 p-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : queue.items.length === 0 ? (
        <div className="rounded-xl border border-white/10 p-10 text-center text-gray-500"
             data-testid="verify-queue-empty">
          No {status} verifications.
        </div>
      ) : (
        <div className="rounded-xl border border-white/10 overflow-hidden"
             data-testid="verify-queue-table">
          <table className="w-full text-sm">
            <thead className="bg-[#131822] text-gray-400 text-xs">
              <tr>
                <th className="text-left px-3 py-2">Player</th>
                <th className="text-left px-3 py-2">Phone</th>
                <th className="text-right px-3 py-2">Cash</th>
                <th className="text-right px-3 py-2">FP</th>
                <th className="text-left px-3 py-2">Submitted</th>
                {status !== 'pending' && <th className="text-left px-3 py-2">Reviewed</th>}
                {status === 'rejected' && <th className="text-left px-3 py-2">Reason</th>}
                {status === 'waiting_sms' && <th className="text-center px-3 py-2">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {queue.items.map((v) => (
                <tr key={v.id} className="border-t border-white/5 hover:bg-white/[0.02]"
                    data-testid={`verify-row-${v.id}`}>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-7 h-7 rounded-full bg-white/10 flex items-center justify-center">
                        <User className="w-3.5 h-3.5 text-gray-400" />
                      </div>
                      <div>
                        <div className="text-white font-medium">{v.player_username || '—'}</div>
                        <div className="text-[11px] text-gray-500">{v.player_email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-gray-200 tabular-nums">{v.phone}</td>
                  <td className="px-3 py-2 text-right text-[#F5D76E] tabular-nums">{fmt(v.wallet?.cash_balance)}</td>
                  <td className="px-3 py-2 text-right text-[#C7D2FE] tabular-nums">{fmtFP(v.wallet?.fp_balance)}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs">{fmtDate(v.submitted_at)}</td>
                  {status !== 'pending' && (
                    <td className="px-3 py-2 text-gray-400 text-xs">
                      <div>{fmtDate(v.reviewed_at)}</div>
                      <div className="text-[10px] text-gray-600">by {v.reviewed_by || '—'}</div>
                    </td>
                  )}
                  {status === 'rejected' && (
                    <td className="px-3 py-2 text-xs text-red-300">{v.rejection_reason || '—'}</td>
                  )}
                  {status === 'waiting_sms' && (
                    <td className="px-3 py-2 text-center">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => approve(v.id)}
                          disabled={!!actioning[v.id]}
                          className="px-3 py-1 rounded-md text-[11px] bg-green-500/20 text-green-300 hover:bg-green-500/30 disabled:opacity-40 flex items-center gap-1"
                          data-testid={`verify-approve-${v.id}`}
                        >
                          {actioning[v.id] === 'approve'
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : <CheckCircle2 className="w-3 h-3" />} Approve
                        </button>
                        <button
                          onClick={() => { setRejectModal(v.id); setRejectReason(''); }}
                          disabled={!!actioning[v.id]}
                          className="px-3 py-1 rounded-md text-[11px] bg-red-500/20 text-red-300 hover:bg-red-500/30 disabled:opacity-40 flex items-center gap-1"
                          data-testid={`verify-reject-${v.id}`}
                        >
                          <XCircle className="w-3 h-3" /> Reject
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Reject reason modal ── */}
      {rejectModal && (
        <div
          className="fixed inset-0 z-[100] bg-black/70 flex items-center justify-center p-3"
          onClick={() => setRejectModal(null)}
          data-testid="verify-reject-modal"
        >
          <div className="w-full md:w-[420px] rounded-xl bg-[#131822] border border-red-500/30 p-5 space-y-3"
               onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-white">Reject verification</h3>
            <textarea
              rows={3}
              placeholder="Reason (shown to the player, optional)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              className="w-full px-3 py-2 bg-[#0f1320] border border-white/10 rounded-lg text-sm text-white focus:outline-none focus:border-red-500 resize-none"
              data-testid="verify-reject-reason-input"
            />
            <div className="flex gap-2">
              <button
                onClick={() => setRejectModal(null)}
                className="flex-1 py-2 rounded-lg border border-white/10 text-sm text-gray-300 hover:bg-white/5"
                data-testid="verify-reject-cancel"
              >Cancel</button>
              <button
                onClick={reject}
                className="flex-1 py-2 rounded-lg text-sm font-bold text-white bg-red-500 hover:bg-red-600"
                data-testid="verify-reject-confirm"
              >Reject</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
